import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react'
import { PopoverContainer } from './styles'
import { MedicalViewsType } from '~/interfaces'
import { TimelineTooltip } from '~/components/shared'
import { IconName } from '~/assets/icons'
import { IconComponent } from '~/assets/icons/component'

export interface PopoverFilterProps {
  children: React.ReactNode
  iconName: IconName
  tooltipDescription: string
  svgFilled?: boolean
  active?: boolean
  selectedOption?: MedicalViewsType | string
  position?: 'left' | 'right' | 'center'
}

export interface PopoverFilterHandles {
  open: () => void
  close: () => void
}

export const PopoverFilter = forwardRef<PopoverFilterHandles, PopoverFilterProps>(
  ({ children, iconName, tooltipDescription, svgFilled, active, selectedOption, position }, ref): JSX.Element => {
    const contentRef = useRef<HTMLDivElement>(null)
    const [isOpen, setIsOpen] = useState(false)

    useEffect(() => {
      const handleClickOutside = (event: MouseEvent): void => {
        if (contentRef.current && !contentRef.current.contains(event.target as Element)) {
          setIsOpen(false)
        }
      }
      const handleCloseWithKeyboard = (event: KeyboardEvent): void => {
        if (event.key === 'Escape') {
          setIsOpen(false)
        }
      }

      if (isOpen) {
        document.addEventListener('click', handleClickOutside)
        document.addEventListener('keydown', handleCloseWithKeyboard)
      }

      return () => {
        document.removeEventListener('keydown', handleCloseWithKeyboard)
        document.removeEventListener('click', handleClickOutside)
      }
    }, [isOpen])

    useEffect(() => {
      if (selectedOption) setIsOpen(false)
    }, [selectedOption])

    const handleOpen = () => {
      setIsOpen(prevState => !prevState)
    }

    useImperativeHandle(ref, () => ({
      open: () => setIsOpen(true),
      close: () => setIsOpen(false)
    }))

    return (
      <PopoverContainer
        ref={contentRef}
        isOpen={isOpen}
        active={!!active || isOpen}
        svgFilled={svgFilled}
        position={position ?? 'center'}
      >
        <TimelineTooltip description={tooltipDescription}>
          <button className="popover-button" onClick={handleOpen} data-testid={`${iconName}-button`}>
            <IconComponent name={iconName} />
          </button>
        </TimelineTooltip>

        <div className="popover-content">{children}</div>
      </PopoverContainer>
    )
  }
)

PopoverFilter.displayName = 'PopoverFilter'
